"use client";

import { CharacterStyle } from "@/app/(home)/create-character/schema";
import { CharacterCard } from "@/app/characters/components/character-card";
import { CharacterCardSkeleton } from "@/app/characters/components/character-card-skeleton";
import { CharactersContainer } from "@/app/characters/components/characters-container";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getPublicCharacters } from "@/actions/characters/get-public-characters";
import {
  IconGenderFemale,
  IconPlus,
  IconSpiral,
  IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";

export function ChooseCompanionModal() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CharacterStyle>(
    CharacterStyle.REALISTIC,
  );

  const { data: characters, isLoading } = useQuery({
    queryKey: ["companion-characters", activeTab],
    queryFn: async () => {
      const { characters } = await getPublicCharacters({
        limit: 20,
        filter: {
          style: activeTab,
        },
      });
      return characters ?? [];
    },
  });

  const tabs = [
    {
      id: CharacterStyle.REALISTIC,
      label: "Realistic",
      icon: IconGenderFemale,
    },
    {
      id: CharacterStyle.ANIME,
      label: "Anime",
      icon: IconSpiral,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          iconOnly
          size="md"
          icon={<IconPlus className="h-5 w-5" />}
          aria-label="Choose AI Companion"
        />
      </DialogTrigger>

      <DialogContent
        widthClassName="max-w-[1400px] w-[95vw] h-[90vh]"
        showCloseButton={false}
        className="border-border bg-card flex flex-col gap-0 overflow-hidden px-0 py-6 dark:border-zinc-800 dark:bg-zinc-900"
      >
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between px-6 pb-6">
          <DialogTitle>Choose your AI companion</DialogTitle>
          <DialogClose>
            <IconX className="size-5" />
          </DialogClose>
        </DialogHeader>
        {/* Header */}
        <div className="border-border mb-4 flex shrink-0 items-center justify-between border-b px-6 pb-4 dark:border-zinc-800">
          <div className="flex flex-1 items-center gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  variant={isActive ? "outline-purple" : "outline"}
                  size="md"
                  className="flex-1 gap-2"
                >
                  <Icon className="size-4" />
                  <span className="font-medium">{tab.label}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1 px-6">
          {isLoading && !characters ? (
            <CharactersContainer>
              {Array.from({ length: 20 }).map((_, index) => (
                <CharacterCardSkeleton key={index} />
              ))}
            </CharactersContainer>
          ) : characters && characters.length > 0 ? (
            <CharactersContainer>
              {characters.map((character) => (
                <CharacterCard
                  key={character.id}
                  character={character}
                  afterStartConversationAction={() => {
                    setOpen(false);
                  }}
                />
              ))}
            </CharactersContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground dark:text-zinc-400">
                No characters found.
              </p>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
